import { useEffect } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { ISelectedTrip, TripDetails } from "redmond";
import { ComponentIdV2 } from "@capone/packages-api";
import { IStoreState } from "../../../reducers/types";
import { useIsPackagesShoppingApiV2Enabled } from "../../../context/experiments";
import {
  getPackageSearchKey,
  getPackagedAirComponentEntry,
} from "../../package-identity/reducer/selectors";
import { getSelectedTrip, getFareTripDetailsById } from "../reducer";
import { fetchPackagesTripDetailsV2 } from "../../itinerary-review/utils/fetchPackagesTripDetailsV2";

export interface FlightTripIdentity {
  /** The package's selected flight trip id, resolved from v0 or v2 — whichever is populated. */
  tripId: string | undefined;
  /** The selected fare id for that trip, resolved from v0 or v2 — whichever is populated. */
  fareId: string | undefined;
  /**
   * The full trip details for `tripId`, read from the shared `tripDetailsById` cache both v0's
   * saga (`fetchPackagesTripDetailsSaga`) and v2's fetch (`fetchPackagesTripDetailsV2`) populate.
   * `undefined` until that fetch resolves.
   */
  tripDetails: TripDetails | undefined;
}

/**
 * Resolves flight trip identity (tripId/fareId/tripDetails) from whichever source is populated —
 * v0's own flight-shop selection, or v2's `GetPackage` snapshot. v0 takes precedence when both are
 * present (matches the resolution order already proven in `ReviewFlightItinerary`/
 * `FlightShopSummaryPanel`/`ReviewFlightDetails`'s containers before this accessor replaced their
 * duplicated inline logic).
 *
 * `tripDetails` is read-only here — it does not trigger a fetch. Use {@link useFlightTripIdentity}
 * from a function component when the caller also needs to trigger the v2 fetch on mount.
 *
 * Memoized via `createSelector` (same pattern as `getPackageHotelIdentity`/
 * `getPackagePricingIdentity`) — without it, every call returns a new object reference, and since
 * `mapStateToProps` re-runs on every Redux dispatch app-wide, that new reference would retrigger
 * any `useEffect` keyed on it (e.g. Book's `INITIALIZE_CHECKOUT_STATE_V2` effect, via
 * `getSelectedTripIdentity` below) on unrelated state changes.
 *
 * Deferred to first invocation (rather than built at module scope) for the same reason as its
 * siblings: some import orders reach this module mid-cycle, before `flight-shop/reducer`'s own
 * selector exports are assigned; building the selector eagerly would capture those `undefined`s.
 */
let memoized: ((state: IStoreState) => FlightTripIdentity) | undefined;

export const getFlightTripIdentity = (state: IStoreState): FlightTripIdentity => {
  if (memoized == null) {
    memoized = createSelector(
      [getSelectedTrip, getPackagedAirComponentEntry, getFareTripDetailsById],
      (selectedTrip, packagedAirComponentEntry, tripDetailsById) => {
        const tripId =
          selectedTrip?.tripId ||
          packagedAirComponentEntry?.component.tripId.value ||
          undefined;
        const fareId =
          selectedTrip?.returnFareId ||
          selectedTrip?.outgoingFareId ||
          packagedAirComponentEntry?.component.fareId?.value ||
          undefined;
        const tripDetails = tripId ? tripDetailsById[tripId] : undefined;

        return { tripId, fareId, tripDetails };
      }
    );
  }

  return memoized(state);
};

/**
 * {@link getFlightTripIdentity}'s tripId/fareId, reshaped into the `ISelectedTrip` checkout
 * (`@capone/checkout`) expects on `flightShop.selectedTrip` — the field `getTripPriceParamsParent`,
 * `getSelectedFareDetails`, `validateUserPassengersService`, etc. all read `tripId`/`fareId` from.
 *
 * Under v2 this is the identity's only source: `flightShop.selectedTrip` is never seeded via Redux
 * (`useCorePackagesSync`'s v2 branch only seeds airports), so without this, checkout initialization
 * depended entirely on `finalize`'s response to backfill it later — a redundant round trip, since
 * `finalize` never returns flight data `GetPackage`'s snapshot doesn't already have (see
 * `finalizePackageService`'s matching comment). Sourcing it here instead means `selectedTrip` is
 * correct from Book's very first render, off the same snapshot that already gates readiness.
 *
 * Returns `undefined` — not an object with `null` fields — when neither tripId nor fareId has
 * resolved yet, matching `getSelectedTrip`'s existing falsy-until-selected contract for v0 (callers
 * like Book's `isReady` rely on this). `outgoingSliceId`/`returnSliceId` are explicitly set to
 * `null` — v2 selects the whole trip as one unit via `fareId`, with no separate slice concept. The
 * fare-rating fields (`outgoingFareRating`/`returnFareRating`) are simply omitted from the returned
 * object (they're optional on `ISelectedTrip`) rather than set to `null` — no v2 consumer reads them.
 */
let memoizedSelectedTripIdentity:
  | ((state: IStoreState) => ISelectedTrip | undefined)
  | undefined;

export const getSelectedTripIdentity = (
  state: IStoreState
): ISelectedTrip | undefined => {
  if (memoizedSelectedTripIdentity == null) {
    memoizedSelectedTripIdentity = createSelector(
      [getFlightTripIdentity],
      ({ tripId, fareId }) => {
        if (tripId == null && fareId == null) return undefined;

        return {
          tripId: tripId ?? null,
          outgoingSliceId: null,
          outgoingFareId: fareId ?? null,
          returnSliceId: null,
          returnFareId: fareId ?? null,
        };
      }
    );
  }

  return memoizedSelectedTripIdentity(state);
};

/**
 * Whether {@link useFlightTripIdentity}'s mount effect should trigger the v2 trip-details fetch:
 * only under v2, once a `tripId` has resolved, and only while `tripDetails` hasn't already
 * landed in the shared cache (so the fetch doesn't re-fire every render once it resolves).
 * Extracted as a pure function so this decision is unit-testable without a hook-rendering harness.
 */
export const shouldFetchFlightTripDetailsV2 = (args: {
  isV2Enabled: boolean;
  tripId: string | undefined;
  tripDetails: TripDetails | undefined;
  searchKey: string | null;
  componentId: ComponentIdV2 | undefined;
}): boolean =>
  args.isV2Enabled &&
  args.tripDetails == null &&
  args.searchKey != null &&
  args.tripId != null &&
  args.componentId != null;

/**
 * {@link getFlightTripIdentity}, plus a mount effect that triggers the v2 trip-details fetch
 * (`fetchPackagesTripDetailsV2`) when running under v2, a `tripId` has resolved, and
 * `tripDetails` hasn't landed in the shared cache yet — the same fetch-trigger pattern
 * `ReviewFlightItinerary`'s mount effect already uses, centralized here so every caller gets it
 * for free instead of needing its own copy (Book's workflow components never had one, which is
 * why their `tripDetails` was always `undefined` under v2).
 *
 * For `connect()`-based containers (which can't call hooks), use {@link getFlightTripIdentity}
 * directly in `mapStateToProps` — but note the fetch will then only fire if some OTHER mounted
 * component in the tree also calls this hook.
 */
export const useFlightTripIdentity = (): FlightTripIdentity => {
  const isV2Enabled = useIsPackagesShoppingApiV2Enabled();
  const identity = useSelector(getFlightTripIdentity);
  const searchKey = useSelector(getPackageSearchKey);
  const packagedAirComponentEntry = useSelector(getPackagedAirComponentEntry);
  const componentId = packagedAirComponentEntry?.componentId;

  useEffect(() => {
    if (
      !shouldFetchFlightTripDetailsV2({
        isV2Enabled,
        tripId: identity.tripId,
        tripDetails: identity.tripDetails,
        searchKey,
        componentId,
      })
    ) {
      return;
    }

    // Guaranteed non-null by shouldFetchFlightTripDetailsV2's checks above.
    fetchPackagesTripDetailsV2({
      searchKey: searchKey as string,
      tripId: identity.tripId as string,
      componentId: (componentId as ComponentIdV2).value,
    });
  }, [isV2Enabled, identity.tripDetails, identity.tripId, searchKey, componentId]);

  return identity;
};
